import React, { useState, useRef } from 'react';
import { Upload, FileUp, X } from 'lucide-react';

interface Props {
  files: File[];
  onFilesChange: (files: File[]) => void;
  id: string;
  compact?: boolean;
}

export default function FileDropArea({ files, onFilesChange, id, compact = false }: Props) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current++;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      onFilesChange([...files, ...Array.from(e.dataTransfer.files)]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onFilesChange([...files, ...Array.from(e.target.files)]);
    }
    // Reset so same file can be selected again
    e.target.value = '';
  };

  const removeFile = (idx: number) => {
    onFilesChange(files.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg ${compact ? 'p-4' : 'p-6'} bg-white transition-colors cursor-pointer ${
          isDragging ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-400'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={handleInputChange}
          className="hidden"
          id={id}
        />
        <div className="flex flex-col items-center gap-2">
          <Upload className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} ${isDragging ? 'text-primary-500' : 'text-gray-400'}`} />
          {isDragging ? (
            <span className="text-sm font-medium text-primary-600">Eliberează pentru a adăuga fișierele</span>
          ) : (
            <>
              <span className="text-sm font-medium text-gray-600">Click sau trage fișiere aici</span>
              {!compact && <span className="text-xs text-gray-400">Se pot selecta mai multe fișiere</span>}
            </>
          )}
        </div>
      </div>
      {files.length > 0 && (
        <div className="mt-2 space-y-1">
          {files.map((file, idx) => (
            <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 text-sm">
              <FileUp className="w-4 h-4 text-primary-500 flex-shrink-0" />
              <span className="flex-1 truncate text-gray-700">{file.name}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">{(file.size / 1024).toFixed(0)} KB</span>
              <button onClick={(e) => { e.stopPropagation(); removeFile(idx); }} className="p-1 text-gray-400 hover:text-red-500 rounded hover:bg-red-50 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
